/**
 * Shared building blocks for the Map Controls panel: a section heading, a
 * labelled control whose label and control both explain themselves on hover,
 * and the select / slider / toggle styling that reads on the dark frosted
 * glass (white fields, white sliders, amber "on" toggles).
 */
import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { ACCENT, ACCENT_HOVER, DARK_BLUE, TEXT, TEXT_DIM, WHITE } from './palette';

/** Uppercase caption naming a group of controls. */
export const PanelSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box sx={{ mb: 1 }}>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        mb: 0.5,
        color: TEXT_DIM,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

type PanelFieldProps = {
  /** Names the label element so the control can point at it (aria-labelledby). */
  id: string;
  label: string;
  /** What the control does, shown on hover of the label and of the control. */
  hint: string;
  children: React.ReactNode;
};

/** A control with its visible label above it and an explanation on hover. The
 * tooltips describe rather than label, so the control's accessible name stays
 * the label text it points at with aria-labelledby. */
export const PanelField: React.FC<PanelFieldProps> = ({ id, label, hint, children }) => (
  <Box sx={{ mb: 1 }}>
    <Tooltip title={hint} placement="right" describeChild>
      <Typography
        id={id}
        variant="body2"
        sx={{ display: 'block', mb: 0.25, color: TEXT, fontSize: 12.5, fontWeight: 600 }}
      >
        {label}
      </Typography>
    </Tooltip>
    {/* Hover-only: MUI's Select hands its blur event on with the target replaced
        by its {value, name}, which the tooltip's focus tracking cannot read (it
        warns on every blur). describeChild keeps the hint on the control for
        assistive technology. */}
    <Tooltip title={hint} placement="right" enterDelay={600} describeChild disableFocusListener>
      <Box>{children}</Box>
    </Tooltip>
  </Box>
);

/** White select on the dark panel, matching the product select. */
export const panelSelectSx = {
  backgroundColor: WHITE,
  fontSize: 13,
  '& .MuiSelect-select': { py: 0.75 },
} as const;

/** White rail/track/thumb so the slider reads on the dark frosted glass. */
export const panelSliderSx = {
  color: WHITE,
  '& .MuiSlider-rail': { backgroundColor: 'rgba(255,255,255,0.35)', opacity: 1 },
  '& .MuiSlider-track': { backgroundColor: WHITE, border: 'none' },
  '& .MuiSlider-thumb': {
    backgroundColor: WHITE,
    '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 6px rgba(255,255,255,0.25)' },
  },
} as const;

/** A full-width toggle that turns amber while on, so its state is unmistakable. */
export const panelToggleSx = {
  width: '100%',
  justifyContent: 'center',
  gap: 0.75,
  py: 0.5,
  fontWeight: 700,
  textTransform: 'none',
  color: WHITE,
  border: `1.5px solid ${WHITE}`,
  backgroundColor: 'transparent',
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
  '&.Mui-selected': {
    color: DARK_BLUE,
    backgroundColor: ACCENT,
    border: `1.5px solid ${ACCENT}`,
    '&:hover': { backgroundColor: ACCENT_HOVER },
  },
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.4)',
    border: '1.5px solid rgba(255,255,255,0.3)',
  },
  '&:focus-visible': { outline: `2px solid ${ACCENT}` },
} as const;
